import {inject, Injectable} from '@angular/core';
import {map, Observable, of, shareReplay} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {SettingsDataService} from './settings-data.service';
import {RefreshService} from './refresh.service';
import {ConfigurationServiceUiSettings, TranslationAppSetting} from './models/ui-settings.model';

const APPLICATION_NAME = 'ConfigurationServiceUI';
const IDENTIFICATION = 'Configuration';

const EMPTY_SETTINGS: ConfigurationServiceUiSettings = {
    translations: [],
    editableApplicationSettings: [],
};

@Injectable({providedIn: 'root'})
export class UiSettingsService {
    private readonly settingsDataService = inject(SettingsDataService);
    private readonly refreshService = inject(RefreshService);
    private readonly translate = inject(TranslateService);

    private cache$?: Observable<ConfigurationServiceUiSettings>;

    constructor() {
        this.refreshService.refresh$.subscribe(() => this.invalidate());
    }

    get settings$(): Observable<ConfigurationServiceUiSettings> {
        this.cache$ ??= this.settingsDataService
            .findAllByIdentificationAndApplicationName({identification: IDENTIFICATION, name: APPLICATION_NAME})
            .pipe(
                map(settings => this.parse(settings[0]?.applicationSettings)),
                catchError(() => {
                    this.invalidate();
                    return of(EMPTY_SETTINGS);
                }),
                shareReplay({bufferSize: 1, refCount: false})
            );

        return this.cache$;
    }

    get translationApps$(): Observable<TranslationAppSetting[]> {
        return this.settings$.pipe(map(settings => settings.translations));
    }

    get editableApplicationSettings$(): Observable<string[]> {
        return this.settings$.pipe(map(settings => settings.editableApplicationSettings));
    }

    translationApp(searchName: string | null | undefined): Observable<TranslationAppSetting | undefined> {
        return this.translationApps$.pipe(
            map(apps => apps.find(app => app.searchName === searchName))
        );
    }

    languagesFor(searchName: string | null | undefined): Observable<string[]> {
        return this.translationApp(searchName).pipe(map(app => app?.languages ?? []));
    }

    languageLabel(language: string): string {
        const key = 'translation.' + language;
        const label = this.translate.instant(key);
        return label === key ? language : label;
    }

    invalidate(): void {
        this.cache$ = undefined;
    }

    private parse(applicationSettings: string | null | undefined): ConfigurationServiceUiSettings {
        if (!applicationSettings) {
            return EMPTY_SETTINGS;
        }

        try {
            const parsed = JSON.parse(applicationSettings);
            return {
                translations: parsed.translations ?? [],
                editableApplicationSettings: parsed.editableApplicationSettings ?? [],
            };
        } catch {
            return EMPTY_SETTINGS;
        }
    }
}
